const { clearOrderSearchCache } = require('@/lib/Orders/orderSearchCacheFuncs');

/**
 * Provides common functionality for what should be done when an order/membership/member barcode is scanned.
 *
 * @constructor
 *
 * @param $timeout
 * @param {NavigationService} navigationService
 * @param {OrderSearcher} orderSearcher
 */
const RedemptionScanHandler = function (
    $timeout,
    navigationService,
    orderSearcher
) {
    this.$timeout = $timeout;
    this.navigationService = navigationService;
    this.orderSearcher = orderSearcher;

    /**
     * @type {?string}
     */
    this.lastScannedMemberRef = null;
};

RedemptionScanHandler.prototype = {
    /**
     * @param {string} query
     * @param {function|null} callback A callback that is provided the search results.
     * @param {boolean} wasBarcodeScan
     */
    search(query, callback, wasBarcodeScan) {
        this.orderSearcher.search({ search: query })
            .then((orderSearchResults) => {
                this.$timeout(() => {
                    const results = orderSearchResults.orders;
                    const wasOnlineSearch = orderSearchResults.wasOnlineSearch;

                    let wasRedirected = this.redirectFromSearchResults(query, results, wasBarcodeScan);
                    if (callback) {
                        callback(query, results, wasOnlineSearch, wasRedirected);
                    }
                });
            });
    },

    /**
     * Check and redirect if the user should be automatically taken to a view page from the results.
     *
     * @param {string} query
     * @param {Order[]} results
     * @param {boolean} wasBarcodeScan
     * @param {boolean} redirectIfOneOrder
     *
     * @return {boolean}
     */
    redirectFromSearchResults: function (query, results, wasBarcodeScan, redirectIfOneOrder = false) {
        let uppercaseQuery = query.toUpperCase();

        // Go through all the orders in the results and extract the memberships from them.
        let membershipsInOrderResults = {};
        let membershipRefsForMemberRef = [];

        for (let o = 0; o < results.length; o++) {
            let thisOrder = results[o];

            let orderMemberships = thisOrder.memberships.filter(function removeDead(membership) {
                return !membership.isDead();
            });

            for (let m = 0; m < orderMemberships.length; m++) {
                let thisMembership = orderMemberships[m];
                let membershipRef = thisMembership.ref.toUpperCase();

                membershipsInOrderResults[membershipRef] = thisMembership;

                // Check if this membership contains a contact with the same ref as the search term.
                let refMember = thisMembership.getMemberByRef(uppercaseQuery);
                if (refMember) {
                    thisMembership.highlightMemberRef = uppercaseQuery;
                    if (membershipRefsForMemberRef.indexOf(membershipRef) === -1) {
                        membershipRefsForMemberRef.push(membershipRef);
                    }
                }
            }
        }

        // If the search query was scanned in and it matches a member's ref, remember
        // that ref was scanned.
        if (wasBarcodeScan && membershipRefsForMemberRef.length > 0) {
            this.setLastScannedMemberRef(uppercaseQuery);
        }

        if (redirectIfOneOrder && membershipRefsForMemberRef.length === 1) {
            // If the orders contained exactly one membership the searched contact/ref appears on,
            // go straight to that membership. Can't redirect if there is more than one membership found
            // because we don't know which one to go to.
            this.navigationService.viewMembershipDetails(membershipRefsForMemberRef[0], uppercaseQuery, true);
            return true;
        } else if (redirectIfOneOrder && membershipsInOrderResults.hasOwnProperty(uppercaseQuery)) {
            // If the memberships contained a membership with a ref the same as the search term,
            // go straight to that membership.
            this.navigationService.viewMembershipDetails(uppercaseQuery, null, true);
            return true;
        } else if (redirectIfOneOrder && results.length === 1 && results[0].bookingRef) {
            // Go straight to the order if there is only one order in the result.

            // Clear the search cache because the user won't be able to get back to the search screen otherwise!
            // (They'd keep being redirected)
            // Note sure if this is still true, but left in to be safe.
            clearOrderSearchCache();
            let queryParams = {};
            if (uppercaseQuery !== results[0].bookingRef) {
                // This is an instance ref not a bookingRef.
                queryParams = {
                    instanceRef: uppercaseQuery,
                };
            }
            this.navigationService.viewOrderDetails(results[0].bookingRef, wasBarcodeScan, queryParams);
            return true;
        }

        if (membershipRefsForMemberRef.length > 0) {
            // Not redirecting straight to the membership for this member ref because there were multiple
            // results. Remember what member ref was scanned.
            this.setLastScannedMemberRef(uppercaseQuery);
        }

        return false;
    },

    /**
     * Instead of using something like 'wasBarcodeScan' in the URL to the memberships page to indicate
     * auto redemption should be triggered, that flag is set here (this is a singleton).
     * This prevents accidentally redeeming a member when refreshing or using the back button as would
     * happen if using the URL.
     *
     * @param {string} memberRef
     */
    setLastScannedMemberRef: function setLastScannedMemberRef(memberRef) {
        this.lastScannedMemberRef = memberRef;
    },

    pullLastScannedMemberRef: function pullLastScannedMemberRef() {
        let ref = this.lastScannedMemberRef;
        this.lastScannedMemberRef = null;

        return ref;
    },
};

module.exports = RedemptionScanHandler;
